import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import serializeJavascript from 'serialize-javascript';

import * as Styled from './seo-breadcrumb.styles';

export type SeoBreadcrumbItem = {
  title: string;
  url: string;
  isExternalUrl?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
};

type Size = 'small' | 'medium' | 'large';

const DEFAULT_SIZE: Size = 'small';

const sizes = {
  small: {
    text: 12,
    icon: 16,
  },
  medium: {
    text: 14,
    icon: 18,
  },
  large: {
    text: 16,
    icon: 20,
  },
} as const;

const SeoBreadCrumbItem: React.FC<{
  isLast: boolean;
  item: SeoBreadcrumbItem;
  size: Size;
}> = ({ isLast, item, size }) => {
  const elementSize = sizes[size];

  return (
    <Styled.SeoBreadcrumbListItem>
      <Styled.TextButton
        $isLast={isLast}
        $fontSize={elementSize.text}
        aria-disabled={isLast}
        aria-current={isLast ? 'page' : undefined}
        size={size}
        to={item.url}
        aria-label={item.title}
      >
        {item.icon ? (
          <item.icon
            width={elementSize.icon}
            height={elementSize.icon}
            role="presentation"
            aria-hidden="true"
          />
        ) : (
          <Styled.TruncatedWrapper aria-hidden="true">
            {item.title}
          </Styled.TruncatedWrapper>
        )}
      </Styled.TextButton>
      {!isLast && (
        <Styled.Separator role="presentation" aria-hidden="true" size={size}>
          &rsaquo;
        </Styled.Separator>
      )}
    </Styled.SeoBreadcrumbListItem>
  );
};

export type SeoBreadcrumbProps = {
  items: SeoBreadcrumbItem[];
  size?: Size;
};

const SeoBreadcrumb: React.FC<SeoBreadcrumbProps> = ({
  items,
  size = DEFAULT_SIZE,
  ...props
}) => {
  const isLastItem = (index: number): boolean => index === items.length - 1;
  const breadcrumbRef = useRef<HTMLOListElement>(null);
  const jsonLDData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': item.url,
        name: item.title,
      },
    })),
  };

  useEffect(() => {
    const checkScrollbar = (): void => {
      if (breadcrumbRef.current) {
        if (
          breadcrumbRef.current.offsetWidth < breadcrumbRef.current.scrollWidth
        ) {
          breadcrumbRef.current.scrollLeft = breadcrumbRef.current.scrollWidth;
        }
      }
    };

    window.addEventListener('resize', checkScrollbar);
    checkScrollbar();

    return () => {
      window.removeEventListener('resize', checkScrollbar);
    };
  }, []);

  return (
    <React.Fragment>
      <Helmet>
        <script type="application/ld+json">
          {serializeJavascript(jsonLDData)}
        </script>
      </Helmet>
      <Styled.SeoBreadcrumbNav aria-label="breadcrumb" {...props}>
        <Styled.SeoBreadcrumbList ref={breadcrumbRef}>
          {items.map((item, index) => (
            <SeoBreadCrumbItem
              key={index}
              item={item}
              size={size}
              isLast={isLastItem(index)}
            />
          ))}
        </Styled.SeoBreadcrumbList>
      </Styled.SeoBreadcrumbNav>
    </React.Fragment>
  );
};

SeoBreadcrumb.displayName = 'SeoBreadcrumb';

export { SeoBreadcrumb };
